/**
 * One registry for every streaming / social platform a BB user or artist can
 * link (Gordon 2026-11-01: "any user can link their streaming accounts to
 * their BB profiles and share them as well").
 *
 * Used by the Social Hub editor (profile → Social), the public profile
 * (/profile/[id]), the artist page and <SocialEmbeds>. Pure TS — no React.
 *
 * Stored shapes we have to read (both exist in Firestore):
 *   users/{uid}.socialLinks        → [{ id?, platform: "Spotify", url, iconUrl? }]  (Social Hub)
 *                                  → { x: '', instagram: '', … }                    (sign-up default)
 *   artists/{id}.socialLinks       → { spotify: 'https://…', instagram: '…' }        (admin forms)
 *   userProfiles/{uid}.socialLinks → same array as users/{uid} — the PUBLIC mirror
 *                                    (users/* is signed-in-only; artist pages are public)
 * Always go through normalizeSocialLinks(); never Object.entries() it raw.
 */

export type PlatformKind = "streaming" | "social" | "web";

export interface PlatformEmbed {
  src: string;
  height: number;
}

export interface SocialPlatform {
  id: string;
  /** Brand name — never translated. */
  label: string;
  kind: PlatformKind;
  /** Offered in the Social Hub editor (the rest are only recognised in older data). */
  inHub: boolean;
  /** Lower-case keys/labels older data used for this platform. */
  aliases?: string[];
  /** Domains shown in hints and errors. */
  domainsHint: string;
  /** Domain used for the favicon fallback. */
  faviconDomain: string;
  /** Placeholder for the link field. */
  example: string;
  /** Does this URL belong to the platform? */
  matches: (u: URL) => boolean;
  /** Build the profile URL from a bare handle. Omitted = a full link is required. */
  fromHandle?: (handle: string) => string;
  /** Short handle/name to show under the label. */
  getHandle?: (u: URL) => string | null;
  /** Key-free public embed, when the platform offers one for this kind of link. */
  embed?: (u: URL) => PlatformEmbed | null;
}

export interface LinkedAccount {
  id: string;
  label: string;
  kind: PlatformKind;
  url: string;
  /** Name from older data for links we can't place (e.g. "RedNote"). */
  customLabel?: string;
}

/** What Social Hub writes to users/{uid}.socialLinks (and the public mirror). */
export interface StoredSocialLink {
  id: string;
  platform: string;
  url: string;
  iconUrl?: string;
}

// ── helpers ─────────────────────────────────────────────────────────────────

export function hostMatches(host: string, domain: string): boolean {
  const h = host.toLowerCase();
  return h === domain || h.endsWith(`.${domain}`);
}

const onDomains = (...domains: string[]) => (u: URL) => domains.some((d) => hostMatches(u.hostname, d));

const firstSegment = (u: URL): string | null => {
  const seg = decodeURIComponent(u.pathname.replace(/^\/+/, "").split("/")[0] || "");
  return seg || null;
};

/** Handles we accept as typed (letters, digits, dot, underscore, dash). */
const HANDLE_RE = /^[A-Za-z0-9._-]{1,100}$/;
/** Same, but safe to inject into third-party embed markup. */
export function isSafeHandle(h: string | null | undefined): h is string {
  return !!h && HANDLE_RE.test(h);
}

const YT_ID_RE = /^[A-Za-z0-9_-]{6,64}$/;

function youtubeEmbed(u: URL): PlatformEmbed | null {
  const v = u.searchParams.get("v");
  if (u.pathname === "/watch" && v && YT_ID_RE.test(v)) {
    return { src: `https://www.youtube-nocookie.com/embed/${v}`, height: 315 };
  }
  if (hostMatches(u.hostname, "youtu.be")) {
    const id = firstSegment(u);
    if (id && YT_ID_RE.test(id)) return { src: `https://www.youtube-nocookie.com/embed/${id}`, height: 315 };
  }
  const list = u.searchParams.get("list");
  if (u.pathname === "/playlist" && list && YT_ID_RE.test(list)) {
    return { src: `https://www.youtube-nocookie.com/embed/videoseries?list=${list}`, height: 315 };
  }
  return null;
}

const TRACKING_PARAMS = ["si", "igsh", "igshid", "fbclid", "gclid", "ref_src", "feature", "nd", "app", "deep_link_value"];

/** Drop tracking noise (Spotify's ?si=, Instagram's ?igsh=, utm_*) and the hash. */
export function cleanUrl(u: URL, upgradeToHttps = true): string {
  const out = new URL(u.toString());
  out.hash = "";
  for (const key of Array.from(out.searchParams.keys())) {
    if (TRACKING_PARAMS.includes(key) || key.startsWith("utm_")) out.searchParams.delete(key);
  }
  if (upgradeToHttps) out.protocol = "https:";
  return out.toString();
}

export function parseHttpUrl(raw: string): URL | null {
  if (!raw || raw.length > 600) return null;
  try {
    const u = new URL(raw.trim());
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    if (!u.hostname.includes(".")) return null;
    return u;
  } catch {
    return null;
  }
}

export function faviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
}

// ── registry ────────────────────────────────────────────────────────────────
// Order = order in the Social Hub editor. YouTube Music MUST stay before
// YouTube (music.youtube.com is also a youtube.com sub-domain).

export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  // ── Streaming ──
  {
    id: "spotify",
    label: "Spotify",
    kind: "streaming",
    inHub: true,
    domainsHint: "open.spotify.com",
    faviconDomain: "open.spotify.com",
    example: "https://open.spotify.com/artist/…",
    matches: onDomains("spotify.com", "spotify.link"),
    getHandle: (u) => u.pathname.match(/\/(?:artist|user)\/([^/?#]+)/)?.[1] ?? null,
    embed: (u) => {
      if (!hostMatches(u.hostname, "open.spotify.com")) return null;
      const m = u.pathname.match(/^\/(?:intl-[a-z]{2}(?:-[a-z]{2})?\/)?(artist|album|track|playlist|episode|show)\/([A-Za-z0-9]+)/i);
      if (!m) return null;
      const type = m[1].toLowerCase();
      return {
        src: `https://open.spotify.com/embed/${type}/${m[2]}?utm_source=generator&theme=0`,
        height: type === "track" || type === "episode" ? 152 : 352,
      };
    },
  },
  {
    id: "applemusic",
    label: "Apple Music",
    kind: "streaming",
    inHub: true,
    aliases: ["apple music", "apple-music", "itunes"],
    domainsHint: "music.apple.com",
    faviconDomain: "music.apple.com",
    example: "https://music.apple.com/…/artist/…",
    matches: onDomains("music.apple.com", "itunes.apple.com"),
    // /se/artist/happy-alright/123 → "happy-alright"
    getHandle: (u) => u.pathname.match(/\/artist\/([^/?#]+)\/\d+/)?.[1] ?? null,
    // Apple embeds albums, playlists, songs, music videos and stations —
    // NOT artist pages (they render blank), so artist links stay link cards.
    embed: (u) => {
      if (!hostMatches(u.hostname, "music.apple.com")) return null;
      const m = u.pathname.match(/^\/(?:[a-z]{2}\/)?(album|playlist|song|music-video|station)\//i);
      if (!m) return null;
      const single = m[1].toLowerCase() === "song" || u.searchParams.has("i");
      return { src: `https://embed.music.apple.com${u.pathname}${u.search}`, height: single ? 175 : 450 };
    },
  },
  {
    id: "youtubemusic",
    label: "YouTube Music",
    kind: "streaming",
    inHub: true,
    aliases: ["youtube music", "youtube-music", "ytmusic"],
    domainsHint: "music.youtube.com",
    faviconDomain: "music.youtube.com",
    example: "https://music.youtube.com/channel/…",
    matches: onDomains("music.youtube.com"),
    embed: youtubeEmbed,
  },
  {
    id: "deezer",
    label: "Deezer",
    kind: "streaming",
    inHub: true,
    domainsHint: "deezer.com, link.deezer.com",
    faviconDomain: "deezer.com",
    example: "https://www.deezer.com/artist/…",
    matches: (u) => onDomains("deezer.com")(u) || ["deezer.page.link", "dzr.page.link"].includes(u.hostname.toLowerCase()),
    // Short share links (link.deezer.com/s/…) can't be resolved in the
    // browser, so only full deezer.com links get the widget.
    embed: (u) => {
      const host = u.hostname.toLowerCase();
      if (!hostMatches(host, "deezer.com") || host.startsWith("link.")) return null;
      const m = u.pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(artist|album|playlist|track)\/(\d+)/i);
      if (!m) return null;
      const type = m[1].toLowerCase();
      return {
        src: `https://widget.deezer.com/widget/auto/${type}/${m[2]}${type === "artist" ? "/top_tracks" : ""}`,
        height: type === "track" ? 150 : 300,
      };
    },
  },
  {
    id: "tidal",
    label: "TIDAL",
    kind: "streaming",
    inHub: true,
    domainsHint: "tidal.com, listen.tidal.com",
    faviconDomain: "tidal.com",
    example: "https://tidal.com/artist/…",
    matches: onDomains("tidal.com"),
  },
  {
    id: "amazonmusic",
    label: "Amazon Music",
    kind: "streaming",
    inHub: true,
    aliases: ["amazon music", "amazon-music"],
    domainsHint: "music.amazon.com",
    faviconDomain: "music.amazon.com",
    example: "https://music.amazon.com/artists/…",
    // music.amazon.com / .co.uk / .de / .se … or amazon.<tld>/music/…
    matches: (u) => {
      const host = u.hostname.toLowerCase();
      if (/^music\.amazon\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host)) return true;
      return /^(www\.)?amazon\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host) && u.pathname.startsWith("/music");
    },
  },
  {
    id: "soundcloud",
    label: "SoundCloud",
    kind: "streaming",
    inHub: true,
    domainsHint: "soundcloud.com",
    faviconDomain: "soundcloud.com",
    example: "yourname",
    matches: onDomains("soundcloud.com"),
    fromHandle: (h) => `https://soundcloud.com/${h}`,
    getHandle: firstSegment,
    embed: (u) => {
      // on.soundcloud.com short links don't resolve inside the widget.
      if (u.hostname.toLowerCase().startsWith("on.") || !firstSegment(u)) return null;
      return {
        src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(u.toString())}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=true`,
        height: 300,
      };
    },
  },
  {
    id: "bandcamp",
    label: "Bandcamp",
    kind: "streaming",
    inHub: true,
    domainsHint: "yourname.bandcamp.com",
    faviconDomain: "bandcamp.com",
    example: "yourname",
    matches: onDomains("bandcamp.com"),
    fromHandle: (h) => `https://${h.toLowerCase()}.bandcamp.com/`,
    getHandle: (u) => {
      const sub = u.hostname.toLowerCase().replace(/\.bandcamp\.com$/, "");
      return sub && sub !== "bandcamp.com" && sub !== "www" ? sub : null;
    },
    // Bandcamp's player needs a numeric album/track id that only their
    // page source has — no key-free embed from a profile link → link card.
  },
  {
    id: "audiomack",
    label: "Audiomack",
    kind: "streaming",
    inHub: true,
    domainsHint: "audiomack.com",
    faviconDomain: "audiomack.com",
    example: "yourname",
    matches: onDomains("audiomack.com"),
    fromHandle: (h) => `https://audiomack.com/${h}`,
    getHandle: firstSegment,
  },
  {
    id: "mixcloud",
    label: "Mixcloud",
    kind: "streaming",
    inHub: true,
    domainsHint: "mixcloud.com",
    faviconDomain: "mixcloud.com",
    example: "yourname",
    matches: onDomains("mixcloud.com"),
    fromHandle: (h) => `https://www.mixcloud.com/${h}/`,
    getHandle: firstSegment,
    embed: (u) => {
      if (!firstSegment(u)) return null;
      const path = u.pathname.replace(/\/?$/, "/");
      return { src: `https://www.mixcloud.com/widget/iframe/?hide_cover=1&light=0&autoplay=0&feed=${encodeURIComponent(path)}`, height: 180 };
    },
  },
  // Recognised in older data only.
  {
    id: "audius",
    label: "Audius",
    kind: "streaming",
    inHub: false,
    domainsHint: "audius.co",
    faviconDomain: "audius.co",
    example: "https://audius.co/…",
    matches: onDomains("audius.co"),
    getHandle: firstSegment,
    embed: (u) => {
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts.length === 2 && parts[1] !== "playlist") {
        return { src: `https://audius.co/embed/track?handle=${encodeURIComponent(parts[0])}&slug=${encodeURIComponent(parts[1])}&flavor=card`, height: 480 };
      }
      if (parts.length === 3 && parts[1] === "playlist") {
        return { src: `https://audius.co/embed/playlist?handle=${encodeURIComponent(parts[0])}&slug=${encodeURIComponent(parts[2])}&flavor=card`, height: 480 };
      }
      return null;
    },
  },
  { id: "beatport", label: "Beatport", kind: "streaming", inHub: false, domainsHint: "beatport.com", faviconDomain: "beatport.com", example: "https://www.beatport.com/artist/…", matches: onDomains("beatport.com") },
  { id: "boomplay", label: "Boomplay", kind: "streaming", inHub: false, domainsHint: "boomplay.com", faviconDomain: "boomplay.com", example: "https://www.boomplay.com/artists/…", matches: onDomains("boomplay.com", "boomplaymusic.com") },

  // ── Social ──
  {
    id: "instagram",
    label: "Instagram",
    kind: "social",
    inHub: true,
    domainsHint: "instagram.com",
    faviconDomain: "instagram.com",
    example: "yourname",
    matches: onDomains("instagram.com", "instagr.am"),
    fromHandle: (h) => `https://instagram.com/${h}`,
    getHandle: firstSegment,
  },
  {
    id: "tiktok",
    label: "TikTok",
    kind: "social",
    inHub: true,
    domainsHint: "tiktok.com",
    faviconDomain: "tiktok.com",
    example: "yourname",
    matches: onDomains("tiktok.com"),
    fromHandle: (h) => `https://tiktok.com/@${h}`,
    getHandle: (u) => u.pathname.match(/^\/@([^/?#]+)/)?.[1] ?? null,
  },
  {
    id: "twitter",
    label: "X (Twitter)",
    kind: "social",
    inHub: true,
    aliases: ["x", "x (twitter)", "x / twitter", "twitter / x"],
    domainsHint: "x.com",
    faviconDomain: "x.com",
    example: "yourname",
    matches: onDomains("x.com", "twitter.com"),
    fromHandle: (h) => `https://x.com/${h}`,
    getHandle: firstSegment,
  },
  {
    id: "youtube",
    label: "YouTube",
    kind: "social",
    inHub: true,
    domainsHint: "youtube.com",
    faviconDomain: "youtube.com",
    example: "yourname",
    matches: (u) => (onDomains("youtube.com", "youtu.be")(u) && !hostMatches(u.hostname, "music.youtube.com")),
    fromHandle: (h) => `https://youtube.com/@${h}`,
    getHandle: (u) =>
      u.pathname.match(/^\/@([^/?#]+)/)?.[1] ??
      u.pathname.match(/^\/(?:c|user)\/([^/?#]+)/)?.[1] ??
      null,
    embed: youtubeEmbed,
  },
  {
    id: "facebook",
    label: "Facebook",
    kind: "social",
    inHub: true,
    domainsHint: "facebook.com",
    faviconDomain: "facebook.com",
    example: "yourpage",
    matches: onDomains("facebook.com", "fb.com", "fb.me"),
    fromHandle: (h) => `https://facebook.com/${h}`,
    getHandle: (u) => {
      const seg = firstSegment(u);
      return seg && seg !== "profile.php" ? seg : null;
    },
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    kind: "social",
    inHub: true,
    domainsHint: "linkedin.com",
    faviconDomain: "linkedin.com",
    example: "yourname",
    matches: onDomains("linkedin.com"),
    fromHandle: (h) => `https://linkedin.com/in/${h}`,
    getHandle: (u) => u.pathname.match(/^\/(?:in|company)\/([^/?#]+)/)?.[1] ?? null,
  },
  {
    id: "bluesky",
    label: "Bluesky",
    kind: "social",
    inHub: false,
    domainsHint: "bsky.app",
    faviconDomain: "bsky.app",
    example: "https://bsky.app/profile/…",
    matches: onDomains("bsky.app"),
    getHandle: (u) => u.pathname.match(/^\/profile\/([^/?#]+)/)?.[1] ?? null,
  },

  // ── Web ──
  {
    id: "website",
    label: "Website",
    kind: "web",
    inHub: true,
    domainsHint: "https://…",
    faviconDomain: "",
    example: "https://yourwebsite.com",
    matches: () => true,
  },
];

const BY_ID = new Map(SOCIAL_PLATFORMS.map((p) => [p.id, p]));

export function getPlatform(id: string): SocialPlatform | undefined {
  return BY_ID.get(id);
}

export const HUB_PLATFORMS = SOCIAL_PLATFORMS.filter((p) => p.inHub);

/** Find a platform by a stored key/label ("Apple Music", "x", "spotify"). */
export function findPlatformByKey(key: string | null | undefined): SocialPlatform | undefined {
  const k = String(key || "").trim().toLowerCase();
  if (!k) return undefined;
  return SOCIAL_PLATFORMS.find((p) => p.id === k || p.label.toLowerCase() === k || p.aliases?.includes(k));
}

/** Which platform does this URL belong to? (never "website") */
export function detectPlatform(u: URL): SocialPlatform | undefined {
  return SOCIAL_PLATFORMS.find((p) => p.kind !== "web" && p.matches(u));
}

/**
 * Turn what someone typed into a profile URL for `platform`, or null when it
 * isn't one. Accepts a handle ("@name", "name") where the platform has
 * handles, or a link — which must be on that platform's own domains.
 */
export function resolvePlatformInput(platform: SocialPlatform, raw: string): string | null {
  const s = (raw || "").trim();
  if (!s) return null;
  const hasProtocol = /^https?:\/\//i.test(s);
  // A dot alone doesn't make a URL — Instagram/TikTok handles contain dots.
  const asUrl = hasProtocol || s.includes("/") || !platform.fromHandle;
  if (asUrl) {
    const u = parseHttpUrl(hasProtocol ? s : `https://${s.replace(/^\/+/, "")}`);
    if (!u) return null;
    if (platform.kind === "web") return cleanUrl(u, false);
    if (!platform.matches(u)) return null;
    return cleanUrl(u);
  }
  const handle = s.replace(/^@+/, "");
  if (!HANDLE_RE.test(handle)) return null;
  if (platform.id === "bandcamp" && !/^[A-Za-z0-9-]{1,63}$/.test(handle)) return null;
  return platform.fromHandle!(handle);
}

export function getHandle(account: Pick<LinkedAccount, "id" | "url">): string | null {
  const p = getPlatform(account.id);
  const u = parseHttpUrl(account.url);
  if (!p?.getHandle || !u) return null;
  try {
    return p.getHandle(u);
  } catch {
    return null;
  }
}

export function getEmbed(account: Pick<LinkedAccount, "id" | "url">): PlatformEmbed | null {
  const p = getPlatform(account.id);
  const u = parseHttpUrl(account.url);
  if (!p?.embed || !u) return null;
  try {
    return p.embed(u);
  } catch {
    return null;
  }
}

/** "www.example.com/path" → "example.com" */
export function displayHost(url: string): string {
  const u = parseHttpUrl(url);
  return u ? u.hostname.replace(/^www\./, "") : url;
}

/**
 * Read any stored socialLinks value (array from Social Hub, map from sign-up
 * or artist docs, or junk) into a clean, de-duplicated list. Only http(s)
 * links survive; the platform is taken from the URL first, then the key.
 */
export function normalizeSocialLinks(...sources: unknown[]): LinkedAccount[] {
  const pairs: Array<{ key: string; url: string }> = [];
  for (const raw of sources) {
    if (Array.isArray(raw)) {
      for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const it = item as Record<string, unknown>;
        if (typeof it.url !== "string") continue;
        pairs.push({ key: String(it.id || it.platform || ""), url: it.url });
      }
    } else if (raw && typeof raw === "object") {
      for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof value === "string") pairs.push({ key, url: value });
      }
    }
  }

  const seen = new Set<string>();
  const out: LinkedAccount[] = [];
  for (const { key, url } of pairs) {
    const u = parseHttpUrl(url);
    if (!u) continue;
    const byKey = findPlatformByKey(key);
    const platform = detectPlatform(u) ?? (byKey && byKey.kind === "web" ? byKey : undefined) ?? getPlatform("website")!;
    const dedupe = `${u.hostname.replace(/^www\./, "").toLowerCase()}${u.pathname.replace(/\/+$/, "")}${u.search}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    const account: LinkedAccount = { id: platform.id, label: platform.label, kind: platform.kind, url: u.toString() };
    // Keep an older custom name ("RedNote", "Linktree") when we can't place the link.
    if (platform.id === "website" && key && !byKey && !/^\d+$/.test(key) && key.length <= 40) account.customLabel = key;
    out.push(account);
  }
  return out;
}

/** Firestore-safe stored form (no undefined fields — the client SDK rejects them). */
export function toStoredLinks(accounts: LinkedAccount[]): StoredSocialLink[] {
  return accounts.map((a) => {
    const p = getPlatform(a.id);
    const stored: StoredSocialLink = { id: a.id, platform: a.customLabel || p?.label || a.label, url: a.url };
    const fav = p?.faviconDomain || displayHost(a.url);
    if (fav) stored.iconUrl = faviconUrl(fav);
    return stored;
  });
}
